'use client';

/**
 * **The Manage Estate Details record, rendered as attribute rows** (VALE-892).
 *
 * The conversion of `edit-matter-dialog.tsx`'s react-hook-form body onto the
 * shared treatment: `matter-attribute-specs.ts`'s field builders laid out
 * through `AttributeSections`, committing one field per blur. It is the same
 * component, the same tokens and the same editors the Contacts drawer renders
 * (VALE-863) and the Overview's estate card renders (VALE-90) — so a coordinator
 * moving between a contact, this drawer and an inventory item cannot tell they
 * were authored separately, which is the whole of what this ticket asked for.
 *
 * This component owns **host policy only**. Everything else is reached rather
 * than restated: the group chrome is `AttributeSections`, the field list is the
 * shared spec builder, the commit-on-blur path is `useAttributeCommit` inside
 * the grid, and the row motion is the grid's.
 *
 * Two things genuinely belong to the host and are here:
 *
 * 1. **The unsent half of the estate bank account.** Its three parts are one
 *    indivisible request field, so a part typed before the other two are valid
 *    has nowhere to go but local state — see
 *    {@link estateBankAccountFields} for why, and {@link bankAccountDraft} for
 *    how the overlay is retired.
 * 2. **The two kept components** — the demo-matter checkbox and the trust-account
 *    note — which carry a paragraph of consequence each and are therefore
 *    `component` blocks continuing the section above them (`title: null`),
 *    rather than rows pretending to be label/value pairs.
 */

import { useCallback, useMemo } from 'react';
import {
  AttributeSections,
  type AttributeBlock,
  type AttributeSpec
} from '@/components/attributes';
import { useIdentityVariantFields } from '@/components/identity-variants';
import { DemoMatterCheckbox } from '@/components/matters/demo-matter-checkbox';
import {
  buildMatterDrawerAttributeBlocks,
  DECEASED_GROUP_TITLE,
  DECEASED_NAME_LABEL,
  ESTATE_BANK_ACCOUNT_GROUP_TITLE,
  MATTER_GROUP_TITLE,
  RESIDENTIAL_ADDRESS_LABEL
} from '@/components/matters/matter-attribute-specs';
import { resolveDemoMatterFlag } from '@/lib/utils/demo-matter';
import type { LawFirmUser } from '@/lib/services/account/types';
import type { Matter, UpdateMatterRequest } from '@/lib/services/matters/types';

export interface MatterEstateAttributeGridProps {
  matter: Matter;
  /** The firm's roster, for the acting-solicitor / assisting-members row. */
  users: LawFirmUser[];
  /**
   * Commits one field's patch. **Must reject to surface a failure**
   * (`components/attributes/CLAUDE.md` §6 — a swallowed rejection is
   * indistinguishable from a save that worked) and must **not** close the
   * drawer.
   */
  onCommit: (patch: UpdateMatterRequest) => Promise<void>;
  /** Fires as a field opens and closes, so the drawer can keep `Escape`. */
  onEditingChange?: (editing: boolean) => void;
}

/**
 * Places `Other names` beside `Deceased` and `Other addresses` beside
 * `Residential address`, as the identity 2×2 the Contact form established.
 *
 * ```
 *   Deceased              Residential address
 *   Other names           Other addresses
 * ```
 *
 * Each scalar sits above the collection that extends it, so a reader scanning
 * the left column gets every name and the right column every address.
 *
 * **Position, not order-by-luck.** The grid flows fields into columns in array
 * order, so the block only holds together if both left-hand rows declare
 * `startsRow` — the spec builder anchors `Deceased`, and the spliced
 * `Other names` is anchored here. Matched on the exported label constants
 * rather than typed literals: the match is on the string, and a restated one
 * goes stale silently when a label changes.
 */
function spliceVariantFields(
  fields: AttributeSpec<UpdateMatterRequest>[],
  variants: AttributeSpec<UpdateMatterRequest>[]
): AttributeSpec<UpdateMatterRequest>[] {
  const [otherNamesField, otherAddressesField] = variants;
  if (!otherNamesField || !otherAddressesField) return fields;
  /*
    A missing anchor is not an error — it simply means this block is not the
    identity block — so an unplaced variant row is dropped rather than appended
    somewhere arbitrary.
  */
  const anchored =
    fields.some((field) => field.label === DECEASED_NAME_LABEL) &&
    fields.some((field) => field.label === RESIDENTIAL_ADDRESS_LABEL);
  if (!anchored) return fields;

  return fields.flatMap((field) => {
    if (field.label === RESIDENTIAL_ADDRESS_LABEL) {
      // The whole second line follows the first, in one step.
      return [
        field,
        { ...otherNamesField, startsRow: true },
        otherAddressesField
      ];
    }
    return [field];
  });
}

export function MatterEstateAttributeGrid({
  matter,
  users,
  onCommit,
  onEditingChange
}: MatterEstateAttributeGridProps) {
  /**
   * **The overlay is dropped only after the commit resolves.**
   *
   * A rejected save must leave it in place, or the parts the reader typed would
   * vanish from the rows on the one gesture that failed to store them — the
   * same reason §6 insists a rejection propagates rather than being swallowed
   * here.
   */
  /*
    Read straight off the matter now that there is no overlay of unsent parts to
    merge over it (VALE-1211): the one popover commits every part together, so
    what the record holds is what the surface shows. Still needed here for the
    "no estate account recorded" note the host splices in beneath the block.
  */
  const storedAccount = matter.estateBankAccount;

  const handleCommit = useCallback(
    async (patch: UpdateMatterRequest) => {
      await onCommit(patch);
    },
    [onCommit]
  );

  /*
    Rebuilt from the latest matter snapshot on every render, which is what makes
    a committed value the value the next render reads: a successful commit
    refetches the matter, the builder re-runs, and the row reads out what was
    actually persisted rather than what was typed.
  */
  const rowBlocks = useMemo(
    () => buildMatterDrawerAttributeBlocks(matter, users),
    [matter, users]
  );

  /*
    `AttributeSpec<never>` — the hook's rows never patch through the grid's
    `onSave`, they own their own endpoints — which is assignable here because
    `toPatch`'s return type is covariant.
  */
  const variantFields: AttributeSpec<UpdateMatterRequest>[] =
    useIdentityVariantFields({
      ownerType: 'matter',
      ownerId: matter.id
    });

  const blocks = useMemo<AttributeBlock<UpdateMatterRequest>[]>(
    () =>
      rowBlocks.flatMap((block) => {
        if (block.title === DECEASED_GROUP_TITLE) {
          return [
            {
              ...block,
              fields: spliceVariantFields(block.fields, variantFields)
            }
          ];
        }

        if (block.title === MATTER_GROUP_TITLE) {
          return [
            block,
            {
              kind: 'component' as const,
              /*
                Continues the Matter section rather than opening one of its own:
                the checkbox is a property of the reference above it, and its own
                heading would say so twice.
              */
              title: null,
              component: (
                <DemoMatterCheckbox
                  id="matter-estate-is-demo"
                  reference={matter.lawFirmInternalReference}
                  checked={matter.isDemo ?? false}
                  onCheckedChange={(checked) => {
                    /*
                      Through the shared resolver, not the raw checkbox state:
                      the backend refuses `isDemo: true` on a reference that does
                      not announce the matter as a demo, and this is the one
                      rule both ends agree on.
                    */
                    void handleCommit({
                      isDemo: resolveDemoMatterFlag(
                        matter.lawFirmInternalReference,
                        checked
                      )
                    });
                  }}
                />
              )
            }
          ];
        }

        if (
          block.title === ESTATE_BANK_ACCOUNT_GROUP_TITLE &&
          !storedAccount?.accountNumber
        ) {
          return [
            block,
            {
              kind: 'component' as const,
              title: null,
              component: (
                <p className="text-muted-foreground text-xs">
                  Until an estate account is recorded here, forms and
                  correspondence are populated with your law firm trust account
                  details.
                </p>
              )
            }
          ];
        }

        return [block];
      }),
    [rowBlocks, variantFields, matter, storedAccount, handleCommit]
  );

  return (
    <AttributeSections<UpdateMatterRequest>
      blocks={blocks}
      onSave={handleCommit}
      onEditingChange={onEditingChange}
      testId="matter-estate-attribute-grid"
      fieldsTestIdPrefix="matter-estate-attribute-fields"
    />
  );
}
